import type { TFunction } from 'i18next';
import type { AllocationRule, RuleTarget } from '@/api/allocationRules';
import { bpToPercentText, formatMoneyMinor } from '@/i18n/format';
import type { OwnedEnvelope } from '@/features/accounts/useOwnedEnvelopes';

export function envelopeLabel(
  envelopes: OwnedEnvelope[],
  id: number,
): { name: string; unknown: boolean } {
  const envelope = envelopes.find((row) => row.id === id);
  if (envelope) return { name: envelope.name, unknown: false };
  return { name: `#${id}`, unknown: true };
}

export function matchSummary(
  rule: AllocationRule,
  currency: string | null,
  locale: string,
  t: TFunction,
): string {
  const bits: string[] = [];
  if (rule.matchPayeeContains !== null && rule.matchPayeeContains.length > 0) {
    bits.push(t('rules.matchContains', { text: rule.matchPayeeContains }));
  }
  if (rule.matchPayeeRegex !== null && rule.matchPayeeRegex.length > 0) {
    bits.push(t('rules.matchRegex', { regex: rule.matchPayeeRegex }));
  }
  const min = rule.matchAmountMinMinor;
  const max = rule.matchAmountMaxMinor;
  if (min !== null && max !== null) {
    bits.push(
      t('rules.matchAmount', {
        min: formatMoneyMinor(min, currency, locale),
        max: formatMoneyMinor(max, currency, locale),
      }),
    );
  } else if (min !== null) {
    bits.push(t('rules.matchAmountMin', { min: formatMoneyMinor(min, currency, locale) }));
  } else if (max !== null) {
    bits.push(t('rules.matchAmountMax', { max: formatMoneyMinor(max, currency, locale) }));
  }
  return bits.join(` ${t('rules.and')} `);
}

export function targetSummary(
  target: Pick<RuleTarget, 'envelopeId' | 'mode' | 'fixedAmountMinor' | 'percentBp'>,
  envelopes: OwnedEnvelope[],
  currency: string | null,
  locale: string,
): string {
  const { name } = envelopeLabel(envelopes, target.envelopeId);
  if (target.mode === 'FIXED') {
    const amount =
      target.fixedAmountMinor !== null
        ? formatMoneyMinor(target.fixedAmountMinor, currency, locale)
        : '';
    return `${name} ${amount}`.trim();
  }
  const percent = target.percentBp !== null ? bpToPercentText(target.percentBp, locale) : '';
  return `${name} ${percent}`.trim();
}

export function targetsSummary(
  targets: AllocationRule['targets'],
  envelopes: OwnedEnvelope[],
  currency: string,
  locale: string,
  transferAccountId?: number | null,
  transferAccountName?: string | null,
  t?: TFunction,
): string {
  if (transferAccountId != null && t) {
    return t('rules.transferTo', {
      account: transferAccountName ?? `#${transferAccountId}`,
    });
  }
  return targets.map((row) => targetSummary(row, envelopes, currency, locale)).join(' · ');
}

const CIVIL = /^(\d{4})-(\d{2})-(\d{2})$/;

/**
 * Today's civil date in an IANA zone. A machine value, never shown: `en-CA` is used only because
 * it yields ISO `YYYY-MM-DD`, the format the API and the date comparisons take.
 */
export function civilInZone(date: Date, timeZone: string): string {
  const zone = timeZone.length > 0 ? timeZone : 'UTC';
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: zone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(date);
}

/** Inclusive span in whole days (`from === to` → 1). */
export function civilSpanDays(from: string, to: string): number | null {
  const a = CIVIL.exec(from);
  const b = CIVIL.exec(to);
  if (a === null || b === null) return null;
  const start = Date.UTC(Number(a[1]), Number(a[2]) - 1, Number(a[3]));
  const end = Date.UTC(Number(b[1]), Number(b[2]) - 1, Number(b[3]));
  return Math.round((end - start) / 86_400_000) + 1;
}
